import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { ProviderId } from 'firebase/auth';
import { z } from 'zod';

import { DialogSlotProps, DialogSlotsComponents } from 'context';

export const SUPPORTED_AUTH_PROVIDER_IDS = [
  ProviderId.PASSWORD,
  ProviderId.GOOGLE,
  'microsoft.com',
  ProviderId.PHONE,
];

export const STATES_URL = 'https://scarlson1.github.io/data/states_20m.json';
export const COUNTIES_URL = 'https://scarlson1.github.io/data/counties_20m.json';

export const stateFIPS = {
  ALABAMA: '01',
  ALASKA: '02',
  ARIZONA: '04',
  ARKANSAS: '05',
  CALIFORNIA: '06',
  COLORADO: '08',
  CONNECTICUT: '09',
  DELAWARE: '10',
  'DISTRICT OF COLUMBIA': '11',
  FLORIDA: '12',
  GEORGIA: '13',
  HAWAII: '15',
  IDAHO: '16',
  ILLINOIS: '17',
  INDIANA: '18',
  IOWA: '19',
  KANSAS: '20',
  KENTUCKY: '21',
  LOUISIANA: '22',
  MAINE: '23',
  MARYLAND: '24',
  MASSACHUSETTS: '25',
  MICHIGAN: '26',
  MINNESOTA: '27',
  MISSISSIPPI: '28',
  MISSOURI: '29',
  MONTANA: '30',
  NEBRASKA: '31',
  NEVADA: '32',
  'NEW HAMPSHIRE': '33',
  'NEW JERSEY': '34',
  'NEW MEXICO': '35',
  'NEW YORK': '36',
  'NORTH CAROLINA': '37',
  'NORTH DAKOTA': '38',
  OHIO: '39',
  OKLAHOMA: '40',
  OREGON: '41',
  PENNSYLVANIA: '42',
  'RHODE ISLAND': '44',
  'SOUTH CAROLINA': '45',
  'SOUTH DAKOTA': '46',
  TENNESSEE: '47',
  TEXAS: '48',
  UTAH: '49',
  VERMONT: '50',
  VIRGINIA: '51',
  WASHINGTON: '53',
  'WEST VIRGINIA': '54',
  WISCONSIN: '55',
  WYOMING: '56',
};

export const COMMISSION_OPTIONS = [
  0.05, 0.06, 0.07, 0.08, 0.09, 0.1, 0.11, 0.12, 0.13, 0.14, 0.15, 0.16, 0.17, 0.18, 0.19, 0.2,
];

export const fallbackImages = [
  'https://firebasestorage.googleapis.com/v0/b/idemand-submissions.appspot.com/o/common%2Fhome-interior-1.jpg?alt=media&token=2d23e76d-2ea4-403e-9f0e-93bbaacebf3e',
  'https://firebasestorage.googleapis.com/v0/b/idemand-submissions.appspot.com/o/common%2Fhome-interior-2.jpg?alt=media&token=720e4102-0c2e-48f9-8b36-c85b0daeaa33',
  'https://firebasestorage.googleapis.com/v0/b/idemand-submissions.appspot.com/o/common%2Fhome-kitchen-1.jpg?alt=media&token=45123914-5cf6-4e2f-976c-76d6009d6371',
  'https://firebasestorage.googleapis.com/v0/b/idemand-submissions.appspot.com/o/common%2Fhome-snow-dusk-1.jpg?alt=media&token=1de02ecb-2dfc-46fb-a2e6-0a223f0d3ac0',
  'https://firebasestorage.googleapis.com/v0/b/idemand-submissions.appspot.com/o/common%2Fhouse-day-1.jpg?alt=media&token=c4395078-19af-4fc0-92da-3d9e2ef6ef37',
  'https://firebasestorage.googleapis.com/v0/b/idemand-submissions.appspot.com/o/common%2Fneighborhood-aerial-1.jpg?alt=media&token=9f80797b-2449-4229-bb2d-b5eb224d86af',
];

export const CONSTRUCTION_TYPE = [
  'frame',
  'masonry',
  'pre-engineered metal',
  'steel frame',
  'protected steel frame',
  'reinforced concrete frame',
];

export const CONTEXT_DIALOG_DEFAULT_SLOTS_COMPONENTS: DialogSlotsComponents = {
  dialog: Dialog,
  title: DialogTitle,
  content: DialogContent,
  actions: DialogActions,
  acceptButton: Button,
  cancelButton: Button,
};

export const CONTEXT_DIALOG_DEFAULT_SLOT_PROPS: DialogSlotProps = {
  dialog: {
    maxWidth: 'xs',
    fullWidth: true,
  },
  title: {},
  content: {
    dividers: true,
  },
  actions: {},
  acceptButton: {
    color: 'primary',
  },
  cancelButton: {
    color: 'secondary',
    variant: 'outlined',
  },
};

// TODO: use zod import types to derive required headers

export const QUOTE_IMPORT_REQUIRED_HEADERS = [
  'product',
  'limitA',
  'limitB',
  'limitC',
  'limitD',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'postal',
  'latitude',
  'longitude',
  'homeState',
  'annualPremium',
  'subproducerCommission',
  'fee1Name',
  'fee1Value',
  'fee2Name',
  'fee2Value',
  'agentName',
  'agentEmail',
  'agentPhone',
  'agentId',
  'agencyName',
  'agencyAddressLine1',
  'agencyAddressLine2',
  'agencyCity',
  'agencyState',
  'agencyPostal',
  'orgId',
  'cbrsDesignation',
  'basement',
  'distToCoastFeet',
  'floodZone',
  'numStories',
  'propertyCode',
  'replacementCost',
  'sqFootage',
  'yearBuilt',
  'aalInland',
  'aalSurge',
  'aalTsunami',
  'techPremiumInland',
  'techPremiumSurge',
  'techPremiumTsunami',
  // TODO: fees
];

export const PORTFOLIO_RATING_REQUIRED_HEADERS = [
  'cov_a_limit',
  'cov_b_limit',
  'cov_c_limit',
  'cov_d_limit',
  'cov_a_rcv',
  'cov_b_rcv',
  'cov_c_rcv',
  'cov_d_rcv',
  'deductible',
  'state',
  'commission_pct',
];

export const POLICY_IMPORT_REQUIRED_HEADERS = [
  'policyId',
  // 'locationId',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'postal',
  'countyName',
  'fips',
  'latitude',
  'longitude',
  'homeState',
  'deductible',
  'limitA',
  'limitB',
  'limitC',
  'limitD',
  'annualPremium',
  'policyPrice',
  'locationEffectiveDate',
  'locationExpirationDate',
  'policyEffectiveDate',
  'policyExpirationDate',
  'term',
  'displayName',
  'firstName',
  'lastName',
  'email',
  'phone',
  'userId',
  'orgId',
  'agentName',
  'agentEmail',
  'agentPhone',
  'agentId',
  'agencyName',
  'agencyId',
  'agencyAddressLine1',
  'agencyAddressLine2',
  'agencyCity',
  'agencyState',
  'agencyPostal',
  'cbrsDesignation',
  'basement',
  'distToCoastFeet',
  'floodZone',
  'numStories',
  'propertyCode',
  'replacementCost',
  'sqFootage',
  'yearBuilt',
  'ffh',
  'product',
  'fee1Name',
  'fee1Value',
  'fee2Name',
  'fee2Value',
  'tax1Name',
  'tax1Value',
  'tax1Rate',
  'tax1SubjectBase',
  'tax2Name',
  'tax2Value',
  'tax2Rate',
  'tax2SubjectBase',
  'mgaCommissionPct',
  'aalInland',
  'aalSurge',
  'aalTsunami',
  'techPremiumInland',
  'techPremiumSurge',
  'techPremiumTsunami',
];

export const TrxImportRow = z.object({
  trxType: z.string(), // TransactionType,
  product: z.string(), // Product,
  policyId: z.string(),
  // locationId: string;
  externalId: z.string(),
  term: z.string(),
  bookingDate: z.string(),
  issuingCarrier: z.string(),
  namedInsured: z.string(),
  mailingAddressLine1: z.string(),
  mailingAddressLine2: z.string(),
  mailingCity: z.string(),
  mailingState: z.string(),
  mailingPostal: z.string(),
  homeState: z.string(),
  insuredAddressLine1: z.string(),
  insuredAddressLine2: z.string(),
  insuredCity: z.string(),
  insuredState: z.string(),
  insuredPostal: z.string(),
  insuredCountyFips: z.string().optional(),
  insuredCountyName: z.string().optional(),
  latitude: z.string(),
  longitude: z.string(),
  otherInterestedParties: z.string(),
  additionalNamedInsured: z.string(),
  // additionalInsureds: string;
  // mortgageeInterest: string;
  policyEffDate: z.string(),
  policyExpDate: z.string(),
  // locationEffDate: string; // include ?? (not in trx ??)
  // locationExpDate: string; // include ??
  trxEffDate: z.string(),
  trxExpDate: z.string(),
  trxDays: z.string(),
  cancelReason: z.string(), // TODO: include cancelEffDate ?? (only needed in policy location)
  locationTermPremium: z.string(),
  locationAnnualPremium: z.string(),
  // TODO: premiumCalcData (only requires mga comm & DWP (but need to update to include reporting ratio fields))
  mgaCommission: z.string(),
  mgaCommissionPct: z.string(),
  netDirectWrittenPremium: z.string(),
  dailyPremium: z.string(),
  termProratedPct: z.string(),
  netErrorAdj: z.string(),
  surplusLinesTax: z.string(),
  surplusLinesRegulatoryFee: z.string(),
  mgaFee: z.string(),
  inspectionFee: z.string(),
  previousPremiumTrxId: z.string(),
  limitA: z.string(),
  limitB: z.string(),
  limitC: z.string(),
  limitD: z.string(),
  deductible: z.string(),
  ratingDocId: z.string(), // TODO: required ?? created from policy import (review endorsement flow)
  replacementCost: z.string(),
  cbrsDesignation: z.string(),
  basement: z.string(),
  distToCoastFeet: z.string(),
  floodZone: z.string(),
  numStories: z.string(),
  propertyCode: z.string(),
  sqFootage: z.string(),
  yearBuilt: z.string(),
  ffh: z.string(),
  units: z.string(),
  tier1: z.string(),
  construction: z.string(),
  priorLossCount: z.string(),
  techPremiumInland: z.string(),
  techPremiumSurge: z.string(),
  techPremiumTsunami: z.string(),
});
export type TTrxImportRow = z.infer<typeof TrxImportRow>;

export const TRANSACTION_IMPORT_REQUIRED_HEADERS = Object.keys(
  TrxImportRow.omit({ insuredCountyFips: true, insuredCountyName: true }).shape
) as (keyof typeof TrxImportRow)[];

// export const TRANSACTION_IMPORT_REQUIRED_HEADERS = [
//   'trxType',
//   'product',
//   'policyId',
//   'externalId',
//   'term',
//   'bookingDate',
//   'issuingCarrier',
//   'namedInsured',
//   'mailingAddressLine1',
//   'mailingAddressLine2',
//   'mailingCity',
//   'mailingState',
//   'mailingPostal',
//   'homeState',
//   'insuredAddressLine1',
//   'insuredAddressLine2',
//   'insuredCity',
//   'insuredState',
//   'insuredPostal',
//   'latitude',
//   'longitude',
//   'otherInterestedParties',
//   'additionalNamedInsured',
//   'policyEffDate',
//   'policyExpDate',
//   'trxEffDate',
//   'trxExpDate',
//   'trxDays',
//   'cancelReason',
//   'locationTermPremium',
//   'locationAnnualPremium',
//   'mgaCommission',
//   'mgaCommissionPct',
//   'netDirectWrittenPremium',
//   'dailyPremium',
//   'termProratedPct',
//   'netErrorAdj',
//   'surplusLinesTax',
//   'surplusLinesRegulatoryFee',
//   'mgaFee',
//   'inspectionFee',
//   'previousPremiumTrxId',
//   'limitA',
//   'limitB',
//   'limitC',
//   'limitD',
//   'deductible',
//   'ratingDocId',
//   'replacementCost',
//   'cbrsDesignation',
//   'basement',
//   'distToCoastFeet',
//   'floodZone',
//   'numStories',
//   'propertyCode',
//   'sqFootage',
//   'yearBuilt',
//   'ffh',
//   'units',
//   'tier1',
//   'construction',
//   'priorLossCount',
//   'techPremiumInland',
//   'techPremiumSurge',
//   'techPremiumTsunami',
// ];
